import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { SearchField, Typography, Box, Backdrop, Stack } from '../../../ui/core';
import { StationIcon } from '../../../ui/icons';

import { searchStations } from '../../../redux/Search/action';

const style = {
  item: {
    padding: '12px 18px',
  },
};

// `backdropTop` accounts for whatever sits above the search on the page: the
// classic 84px AppBar (default, preserves existing callers), or 0 on `flush`
// pages that have no AppBar at all.
const StationsSearch = ({ backdropTop = 84 }) => {
  const [inputValue, setInputValue] = useState('');
  const [showBackdrop, setShowBackdrop] = useState(false);
  const { data: stations, loading } = useSelector((state) => state.search.stations);
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();

  useEffect(() => {
    const timer = setTimeout(() => {
      searchStations({ q: inputValue })(dispatch);
    }, 500);
    return () => clearTimeout(timer);
  }, [inputValue, dispatch]);

  /**
   * Preserves the whole existing query string and sets only the two params this
   * handler owns.
   *
   * It used to rebuild the string from an enumerated list (`page`, `city`,
   * `stationId`, `fullScreen`), which silently dropped everything not on that
   * list — so picking a station reset the grid's `sortBy`/`sortDir` and the rows
   * behind the dialog refetched in the server's default order. Enumerating is
   * the bug: this component sits next to a table that owns params it knows
   * nothing about, and the list goes stale every time one is added. Copying the
   * current search means a param added later is carried without touching this
   * file.
   */
  const selectStation = async (_, value) => {
    setInputValue(inputValue);
    const params = new URLSearchParams(search);
    params.set('stationId', value.id);
    params.set('fullScreen', false);
    history.push({ search: params.toString() });
  };

  return (
    <div>
      <SearchField
        id="stations_search"
        sx={{ width: 500 }}
        placeholder="Search"
        value={null}
        inputValue={inputValue}
        options={stations}
        getOptionLabel={(station) => station.name || station.address || station.id}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onChange={selectStation}
        renderOption={(props, station) => (
          <Box component="li" key={station.id} {...props} sx={style.item}>
            <StationIcon sx={{ fontSize: 14 }} />
            <Stack spacing="8px" marginLeft="10px">
              <Typography component="span">{station.name}</Typography>
              <Typography component="span" color="text.secondary" variant="small">
                {station.address}
              </Typography>
            </Stack>
          </Box>
        )}
        blurOnSelect
        loading={loading}
        onOpen={() => setShowBackdrop(true)}
        onClose={() => setShowBackdrop(false)}
      />
      <Backdrop open={showBackdrop} sx={{ top: `${backdropTop}px`, marginLeft: 0 }} />
    </div>
  );
};

export default StationsSearch;
